import { useState } from 'react';
import { useBonds } from '../../hooks/useBonds';
import { useBondFilters } from '../../hooks/useBondFilters';
import { useSaveBonds } from '../../hooks/useSaveBonds';
import BondTypeTabs from './BondTypeTabs';
import BondInfoHeader from './BondInfoHeader';
import BondsDataTable from './BondsDataTable';
import type { Bond, BondsMetadata } from '../../types/bonds';
import React from 'react';

interface BondsTableProps {
  uploadedBonds?: Bond[] | null;
  uploadedFileName?: string | null;
  onPageRefresh?: () => void;
  onMetadataReceived?: (metadata: BondsMetadata) => void;
}

export default function BondsTable({ uploadedBonds, uploadedFileName, onPageRefresh, onMetadataReceived }: BondsTableProps) {
  const { data: bondsData, isLoading, error, refetch } = useBonds();
  const [selectedType, setSelectedType] = useState<string>('');
  const [localBonds, setLocalBonds] = useState<Bond[] | undefined>(bondsData?.bonds);

  const { saveBonds, isSaving, saveError, saveSuccess } = useSaveBonds();

  // Update local bonds when API data changes or when uploaded bonds are received
  React.useEffect(() => {
    if (uploadedBonds) {
      // Use uploaded bonds if available
      setLocalBonds(uploadedBonds);

      // Set the first available bond type as default when bonds are loaded
      if (uploadedBonds.length > 0 && !selectedType) {
        const uniqueTypes = [...new Set(uploadedBonds.map(bond => bond.type))].sort();
        if (uniqueTypes.length > 0) {
          setSelectedType(uniqueTypes[0]);
        }
      }
    } else if (bondsData?.bonds) {
      // Use API bonds if no uploaded bonds
      setLocalBonds(bondsData.bonds);

      // Set the first available bond type as default when bonds are loaded
      if (bondsData.bonds.length > 0 && !selectedType) {
        const uniqueTypes = [...new Set(bondsData.bonds.map(bond => bond.type))].sort();
        if (uniqueTypes.length > 0) {
          setSelectedType(uniqueTypes[0]);
        }
      }

      // Pass metadata to parent component
      if (onMetadataReceived && bondsData.metadata) {
        onMetadataReceived(bondsData.metadata);
      }
    }
  }, [bondsData, uploadedBonds, selectedType, onMetadataReceived]);

  const { selectedBondInfo, periodsForSelectedTab } = useBondFilters(localBonds, selectedType);

  const handleBondsChange = (updatedBonds: Bond[]) => {
    // Update local bonds when table data is edited
    setLocalBonds(updatedBonds);

    // Check if the selected type still exists in the updated bonds
    // If not, select the first available type
    const uniqueTypes = [...new Set(updatedBonds.map(bond => bond.type))].sort();
    if (uniqueTypes.length > 0 && !uniqueTypes.includes(selectedType)) {
      setSelectedType(uniqueTypes[0]);
    } else if (uniqueTypes.length === 0) {
      // Jeśli nie ma żadnych obligacji, zresetuj wybrany typ
      setSelectedType('');
    }
  };

  const handleSave = async (updatedBonds: Bond[]) => {
    const result = await saveBonds(updatedBonds, uploadedFileName || undefined, refetch, onPageRefresh);
    if (result) {
      // Update local bonds with the response from server
      setLocalBonds(result);
    }
  };

  if (isLoading) return <div className="loading"><div className="spinner"></div><p>Ładowanie danych...</p></div>;
  if (error) return <div className="error"><h2>Błąd ładowania</h2><p>{error.message}</p><button onClick={() => refetch()}>Spróbuj ponownie</button></div>;

  return (
    <div>
      {uploadedBonds && (
        <div className="uploaded-data-notification">
          <span>📄 Wyświetlane są dane z wgranego pliku. Kliknij "Odśwież z API" aby załadować dane z serwera.</span>
        </div>
      )}
      {saveSuccess && (
        <div className="save-notification">
          <span>✅ {saveSuccess}</span>
        </div>
      )}
      {saveError && (
        <div className="save-notification error">
          <span>❌ {saveError}</span>
        </div>
      )}

      <BondTypeTabs
        bonds={localBonds}
        selectedType={selectedType}
        onTypeChange={setSelectedType}
      />

      <BondInfoHeader
        name={selectedBondInfo?.name || ''}
        type={selectedBondInfo?.type || ''}
      />

      <BondsDataTable
        bonds={localBonds || []}
        selectedType={selectedType}
        periodsForSelectedTab={periodsForSelectedTab}
        onBondsChange={handleBondsChange}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  );
} 